import { isAccountLanguage, type AccountLanguage } from "../../shared/accountLanguage";

type LanguageWrite = {
    accountId: string;
    language: AccountLanguage;
    initializeOnly: boolean;
};

export class AccountLanguageSync {
    private accountId: string | null = null;
    private generation = 0;
    private choiceRevision = 0;
    private writes: Promise<void> = Promise.resolve();

    constructor(private readonly options: {
        getLanguage: () => AccountLanguage;
        applyLanguage: (language: AccountLanguage) => void;
        save: (write: LanguageWrite) => Promise<{ language: unknown }>;
        onError: (error: unknown) => void;
    }) {}

    async restore(accountId: string | null, savedLanguage: unknown): Promise<void> {
        if (accountId && this.accountId === accountId) return;
        this.accountId = accountId;
        const generation = ++this.generation;
        if (!accountId) return;

        if (isAccountLanguage(savedLanguage)) {
            this.options.applyLanguage(savedLanguage);
            return;
        }

        // Initialize atomically: a fresh browser must not overwrite another device's saved choice.
        const choiceRevision = this.choiceRevision;
        await this.enqueue({ accountId, language: this.options.getLanguage(), initializeOnly: true }, generation,
            (language) => {
                if (this.choiceRevision === choiceRevision) this.options.applyLanguage(language);
            });
    }

    changed(language: AccountLanguage): Promise<void> {
        ++this.choiceRevision;
        if (!this.accountId) return Promise.resolve();
        return this.enqueue({ accountId: this.accountId, language, initializeOnly: false }, this.generation);
    }

    disconnect(): void {
        this.accountId = null;
        ++this.generation;
    }

    private enqueue(write: LanguageWrite, generation: number,
        onSaved?: (language: AccountLanguage) => void): Promise<void> {
        // Serialize toggles so a slower earlier request cannot become the final saved preference.
        this.writes = this.writes.then(async () => {
            if (generation !== this.generation) return;
            try {
                const result = await this.options.save(write);
                if (!isAccountLanguage(result.language)) throw new Error("Invalid account language response.");
                if (generation === this.generation) onSaved?.(result.language);
            } catch (error) {
                this.options.onError(error);
            }
        });
        return this.writes;
    }
}
